/*
 *******************************************************************************
 *
 *  Filename:   @/components/auth/SignIn.js
 *
 *  Syntax:     NA
 *
 *  Synopsis:   Module for auth sign in.
 *
 *  Author:     Norman J. Nolasco [ Archetact Software ]
 *
 *  Created:    Friday, August 18, 2023 - 6:07 AM (CST)
 *
 *  Notes:
 *
 *  Revisions:
 *              2023/08/18  NJN Initial Build
 *
 *  Copyright (c) 2023 - Archetact Software - All Rights Reserved.
 *  Unauthorized copying of this file via any medium is strictly prohibited.
 *  Proprietary and Confidential.
 *
 *******************************************************************************
 */

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "@emotion/styled";
import { Link } from "react-router-dom";
import * as Yup from "yup";
import { Formik } from "formik";

import {
  Alert as MuiAlert,
  Box,
  Button,
  //Checkbox,
  //FormControlLabel,
  TextField as MuiTextField,
  Typography,
} from "@mui/material";
import { spacing } from "@mui/system";

import useAuth from "@/hooks/useAuth";
import NewPasswordReset from "./NewPasswordReset";
import RedirectReset from "./RedirectReset";

const Alert = styled(MuiAlert)(spacing);

const TextField = styled(MuiTextField)(spacing);

function SignIn() {
  const navigate = useNavigate();
  const { signIn, completeNewPasswordChallenge, session } = useAuth();
  const [openDialog, setOpenDialog] = useState(false);
  const [openRedirectReset, setOpenRedirectReset] = useState(false);

  const changePassword = async (email, password) => {
    try {
      const newPasswordResult = await completeNewPasswordChallenge(email, password, session);
      console.log("newPasswordResult", newPasswordResult);
    } catch (error) {
      console.log("ERROR CHANGING PASSWORD");
      return error;
    }
  };

  return (
    <Formik
      name="loginForm"
      initialValues={{
        email: "",
        password: "",
        submit: false,
      }}
      validationSchema={Yup.object().shape({
        email: Yup.string().email("Must be a valid email").max(255).required("Email is required"),
        password: Yup.string().max(255).required("Password is required"),
      })}
      onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
        try {
          const result = await signIn(values.email, values.password);
          console.log("signIn result", result);

          if (result.newPasswordRequired) {
            setOpenDialog(true);
          } else if (result.resetPasswordRequired) {
            setOpenRedirectReset(true);
          } else if (result.isAuthenticated === false) {
            setSubmitting(false);
            setStatus({ success: false });
            setErrors({ submit: "Login failed. Invalid credentials." });
          } else {
            console.log("User logged in successfully:", result);
            navigate("/dashboard/default");
          }
        } catch (error) {
          const message = error.message || "Something went wrong";
          if (error.code === "UserNotConfirmedException") {
            setConfirmEmailIsVisible("block");
          }
          setSubmitting(false);
          setStatus({ success: false });
          setErrors({ submit: message });
        }
      }}
    >
      {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
        <form noValidate onSubmit={handleSubmit}>
          {errors.submit && (
            <Alert mt={2} mb={3} severity="warning">
              {errors.submit}
            </Alert>
          )}
          <TextField
            type="email"
            name="email"
            label="Email Address"
            value={values.email}
            error={Boolean(touched.email && errors.email)}
            fullWidth
            helperText={touched.email && errors.email}
            onBlur={handleBlur}
            onChange={handleChange}
            my={2}
          />
          <TextField
            type="password"
            name="password"
            label="Password"
            value={values.password}
            error={Boolean(touched.password && errors.password)}
            fullWidth
            helperText={touched.password && errors.password}
            onBlur={handleBlur}
            onChange={handleChange}
            my={2}
          />
          {/*
          <FormControlLabel
            control={<Checkbox value="remember" color="primary" />}
            label="Remember me"
          />
          */}
          <Box my={2}>
            <Button type="submit" fullWidth variant="contained" color="primary" disabled={isSubmitting}>
              Sign in
            </Button>
          </Box>
          <Box>
            <Button component={Link} to="/auth/reset-password" fullWidth color="primary">
              Forgot password
            </Button>
          </Box>
          <NewPasswordReset
            open={openDialog}
            navigate={navigate}
            setOpen={setOpenDialog}
            changePassword={changePassword}
            email={values.email}
          />
          <RedirectReset open={openRedirectReset} navigate={navigate} setOpen={setOpenRedirectReset} />
        </form>
      )}
    </Formik>
  );
}

export default SignIn;
